import { alpha, Box, Button, Stack, Tooltip } from '@mui/material';
import IconMi from 'components/common/icon/IconMi';
import React from 'react';
import { EventCalendarType } from 'components/metric/MetricViewer';
import useBundleTranslation from 'i18n';
import CollaborationEvent from 'components/metric/collaboration-controls/CollaborationEvent';
import { MetricChartCompareLineType, MetricChartOverlayType } from 'components/metric/chart';
import OverlayMetrics from 'components/metric/collaboration-controls/overlay-metrics/OverlayMetrics';
import CollaborationAnnotation from 'components/metric/collaboration-controls/CollaborationAnnotation';
import useWallContext from 'components/wall/hooks/useWallContext';
import { useIsAdminOrHasPrivilege } from 'hooks/useUserPrivilege';

export default function MetricChartCollaborationControls({
    manualCalendars,
    elementId,
    segmentValueId,
    userChartOverlay,
    addCompareLines,
    removeCompareLines,
    updateCompareLine,
    addOverlays,
    removeOverlays,
    updateOverlay,
    onReset,
    refetch,
    expertCommentaryAllowEdit,
    showChartOverlay = true,
    showAddEvent = true,
    showAddAnnotation = true,
}: {
    manualCalendars: Array<EventCalendarType>;
    elementId: number;
    segmentValueId: number;
    userChartOverlay: number;
    addCompareLines: (compareLines: Array<MetricChartCompareLineType>) => void;
    removeCompareLines: (compareLines: Array<number>) => void;
    updateCompareLine: (id: number, color: string, dashStyle: string) => void;
    addOverlays: (overlays: Array<MetricChartOverlayType>) => void;
    removeOverlays: (overlays: Array<{ elementId: number; segmentValueId: number }>) => void;
    updateOverlay: (overlay: MetricChartOverlayType) => void;
    onReset: () => void;
    refetch: () => void;
    expertCommentaryAllowEdit: boolean;
    showChartOverlay?: boolean;
    showAddEvent?: boolean;
    showAddAnnotation?: boolean;
}) {
    const { t } = useBundleTranslation('components/metric/chart');
    const { wallActions } = useWallContext(false);

    const canAddAnnotation = useIsAdminOrHasPrivilege('PRIV_ADD_ANNOTATION');

    return (
        <>
            <Stack
                direction="row"
                alignItems="center"
                sx={{
                    pt: 3,
                    containerType: 'inline-size',
                    containerName: 'chart-controls',
                }}
            >
                <Stack direction="row" alignItems="center" sx={{ flex: '1 1 0' }}>
                    {expertCommentaryAllowEdit && (
                        <Tooltip title={t('add_expert_analysis')}>
                            <Button
                                sx={{
                                    '&.MuiButton-root:not(:hover)': {
                                        borderColor: (theme) => alpha(theme.palette.primary.main, 0.4),
                                    },
                                    '@container chart-controls (width < 580px)': {
                                        '& .expert-analysis-label': { display: 'none' },
                                        '& .MuiButton-startIcon': { mr: 0, ml: 0 },
                                        minWidth: 0,
                                        px: '7px',
                                    },
                                }}
                                data-test={'add_expert_analysis_button'}
                                variant="outlined"
                                startIcon={<IconMi icon="expert-analysis" />}
                                onClick={() => {
                                    wallActions.setExpertAnalysisField(true);
                                }}
                            >
                                <span className="expert-analysis-label">{t('add_expert_analysis')}</span>
                            </Button>
                        </Tooltip>
                    )}
                </Stack>
                <Stack
                    direction="row"
                    sx={{
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}
                >
                    {showChartOverlay && (
                        <OverlayMetrics
                            elementId={elementId}
                            segmentValueId={segmentValueId}
                            userChartOverlay={userChartOverlay}
                            addCompareLines={addCompareLines}
                            removeCompareLines={removeCompareLines}
                            updateCompareLine={updateCompareLine}
                            addOverlays={addOverlays}
                            removeOverlays={removeOverlays}
                            updateOverlay={updateOverlay}
                        />
                    )}

                    {showAddEvent && manualCalendars.length > 0 && (
                        <CollaborationEvent
                            afterSave={() => refetch()}
                            eventId={0}
                            elementId={elementId}
                            segmentValueId={segmentValueId}
                        >
                            <Button
                                sx={{
                                    mx: 0.5,
                                    '@container chart-controls (width < 715px)': {
                                        '&': {
                                            mx: 0.25,
                                            px: '9px',
                                        },
                                    },
                                }}
                                variant="light"
                                startIcon={<IconMi icon="add-event" />}
                            >
                                {t('collaboration_add_event')}
                            </Button>
                        </CollaborationEvent>
                    )}

                    {showAddAnnotation && canAddAnnotation && (
                        <CollaborationAnnotation
                            annotationId={0}
                            elementId={elementId}
                            segmentValueId={segmentValueId}
                            metricInstanceId={0}
                            afterSave={() => refetch()}
                        >
                            <Button
                                sx={{
                                    mx: 0.5,
                                    '@container chart-controls (width < 715px)': {
                                        '&': {
                                            mx: 0.25,
                                            px: '9px',
                                        },
                                    },
                                }}
                                variant="light"
                                startIcon={<IconMi icon="add-annotation" />}
                            >
                                {t('collaboration_add_annotation')}
                            </Button>
                        </CollaborationAnnotation>
                    )}
                </Stack>
                <Stack direction="row" alignItems="center" justifyContent="flex-end" sx={{ flex: '1 1 0' }}>
                    <Tooltip title={t('revert_changes')}>
                        <Button
                            sx={{
                                '&.MuiButton-root:not(:hover)': {
                                    borderColor: (theme) => alpha(theme.palette.primary.main, 0.4),
                                },
                            }}
                            variant="outlined"
                            onClick={onReset}
                            className={'min-width--icon'}
                        >
                            <IconMi icon="rotate-ccw" sx={{ fontSize: '16px' }} />
                        </Button>
                    </Tooltip>
                </Stack>
            </Stack>
        </>
    );
}
